import React, { ComponentProps, useCallback, useContext, useEffect, useRef, useState } from "react";
import ReactMarkdownPreview from "@uiw/react-markdown-preview/nohighlight";
import "@uiw/react-markdown-preview/markdown.css";
import "@uiw/react-md-editor/markdown-editor.css";
import rehypeSanitize from "rehype-sanitize";
import rehypeHighlight from "rehype-highlight";

const CopyTransformContext = React.createContext<((text: string) => string) | undefined>(undefined);

function CodeBlockPre({ children, ...props }: React.HTMLAttributes<HTMLPreElement>) {
    const [copied, setCopied] = useState(false);
    const preRef = useRef<HTMLPreElement>(null);
    const onCopy = useContext(CopyTransformContext);

    const handleCopy = useCallback(() => {
        const rawCode = preRef.current?.querySelector("code")?.textContent || "";
        const code = onCopy ? onCopy(rawCode) : rawCode;
        navigator.clipboard.writeText(code);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    }, [onCopy]);

    return (
        <div className="group/code relative">
            <pre ref={preRef} {...props}>
                {children}
            </pre>
            <button
                onClick={handleCopy}
                className="absolute right-2 top-2 flex h-7 w-7 items-center justify-center rounded border border-gray-300 bg-white text-xs text-gray-500 opacity-0 shadow-sm transition-opacity hover:bg-gray-100 hover:text-gray-700 group-hover/code:opacity-100"
                aria-label="Copy code"
            >
                <i className={`far fa-fw ${copied ? "fa-check text-green-400" : "fa-copy"}`} />
            </button>
        </div>
    );
}

export const MarkdownPreview = ({
    source,
    colorMode = "light",
    sanitize = true,
    onCopy,
    components: userComponents,
    ...props
}: ComponentProps<typeof ReactMarkdownPreview> & {
    source: string;
    colorMode?: "light" | "dark";
    sanitize?: boolean;
    onCopy?: (text: string) => string;
}) => {
    const rehypePlugins = [sanitize ? rehypeSanitize : undefined, rehypeHighlight].filter(Boolean);
    const containerRef = useRef<HTMLDivElement>(null);
    const components = React.useMemo(
        () => ({ pre: CodeBlockPre, ...userComponents }),
        [userComponents]
    );

    useEffect(() => {
        if (!onCopy) {
            return;
        }

        const handleCopyEvent = (event: ClipboardEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                return;
            }

            const selection = window.getSelection();
            if (selection && selection.toString().length > 0) {
                const selectedText = selection.toString();
                const transformedText = onCopy(selectedText);

                if (transformedText !== selectedText) {
                    event.clipboardData?.setData("text/plain", transformedText);
                    event.preventDefault();
                }
            }
        };

        document.addEventListener("copy", handleCopyEvent);

        return () => {
            document.removeEventListener("copy", handleCopyEvent);
        };
    }, [onCopy]);

    return (
        <CopyTransformContext.Provider value={onCopy}>
            <div data-color-mode={colorMode} ref={containerRef}>
                <ReactMarkdownPreview
                    source={source}
                    rehypePlugins={rehypePlugins}
                    components={components}
                    {...props}
                    className={`force-purple-links markdownStyling workflowsMarkdownStyling ${props.className || ""}`}
                    style={{ fontFamily: "Inter", ...props.style }}
                />
            </div>
        </CopyTransformContext.Provider>
    );
};
